import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { TenantConfig } from "../../../types/tenantConfig";
import DefaultConfig from "../../../util/defaultConfig";
import { TenantConfig as TConfig } from "../../../types/tenantConfig";
import { RootState } from "../../store";
import { TenantConfig as resolveTenantConfig } from "../../../util/tenantConfig";

// Define the interface for your config state
export interface ConfigState {
  config: TenantConfig;
  /**
   * False until the tenant-specific config has been loaded into the slice
   * (either via `loadTenantConfig.fulfilled` or `setTenantConfiguration`).
   * Service-gating consumers must wait for this before deciding to redirect,
   * otherwise a direct-paste URL can fall through to DefaultConfig.services
   * and trigger a redirect that the now-loading tenant config would have
   * allowed.
   */
  configLoaded: boolean;
}

// Initial state
const initialState: ConfigState = {
  config: DefaultConfig,
  configLoaded: false,
};

export const loadTenantConfig = createAsyncThunk(
  "config/loadTenantConfig",
  async () => {
    // Delegates to the central resolver: runtime API on the shared deployment,
    // build-time folder import on legacy/dev builds. Falls back to DefaultConfig
    // internally, so this never rejects.
    return (await resolveTenantConfig()) as TenantConfig;
  }
);

// Create the config slice
const configSlice = createSlice({
  name: "configSlice",
  initialState,
  reducers: {
    setTenantConfiguration: (state, action: PayloadAction<TConfig>) => {
      state.config = action.payload;
      state.configLoaded = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      loadTenantConfig.fulfilled,
      (state, action: PayloadAction<TenantConfig>) => {
        state.config = action.payload;
        state.configLoaded = true;
      }
    );
  },
});

// Selectors
export const selectConfig = (state: RootState) => state.tenantConfig.config;
export const selectToolbarItems = (state: RootState) =>
  state.tenantConfig.config.toolbarItems;
// Export the actions

export const { setTenantConfiguration } = configSlice.actions;

// Export the reducer
export default configSlice.reducer;
